import { useState } from "react";
import { Plus, Sparkles, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cartStore } from "@/lib/cart";
import { useLang, useT, productName, productDescription, uomLabel, offerLabel } from "@/lib/i18n";

export default function ProductCard({ product }) {
  const [qty, setQty] = useState("");
  const [imgErr, setImgErr] = useState(false);
  const { lang } = useLang();
  const t = useT();

  const hasOffer = !!product.offer;
  const basePrice = Number(product.price);
  const finalPrice = hasOffer
    ? Number((basePrice * (1 - Number(product.offer.discount_percent) / 100)).toFixed(2))
    : basePrice;

  const displayName = productName(product, lang);
  const displayUom = uomLabel(product.uom, lang);
  const displayDescription = productDescription(product, lang);
  const displayOfferLabel = offerLabel(product.offer, lang);

  const addToCart = () => {
    const val = parseFloat(qty);
    if (!qty || isNaN(val) || val <= 0) {
      toast.error(t("invalid_qty"));
      return;
    }
    // Store English UoM in cart so backend recomputes correctly; frontend renders localized when displaying
    const qtyText = `${qty} ${product.uom}`;
    cartStore.add({
      product_id: product.id,
      name: product.name,
      name_ta: product.name_ta || null,
      uom: product.uom,
      image_url: product.image_url,
      quantity_text: qtyText,
      quantity_value: val,
      unit_price: finalPrice,
      line_total: Number((finalPrice * val).toFixed(2)),
    });
    toast.success(`${t("add_success")}: ${qty} ${displayUom} · ${displayName}`);
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-2xl border border-black/5 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_60px_-30px_rgba(27,67,50,0.4)] transition-transform duration-300"
    >
      <div className="relative aspect-[4/3] bg-stone overflow-hidden">
        {product.image_url && !imgErr ? (
          <img
            src={product.image_url}
            alt={displayName}
            onError={() => setImgErr(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-moss/50">
            <ImageOff className="w-8 h-8" />
          </div>
        )}
        {hasOffer && (
          <div
            data-testid={`offer-badge-${product.id}`}
            title={displayOfferLabel}
            className="offer-shine absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase px-3 py-1.5 rounded-full"
          >
            <Sparkles className="w-3 h-3" />
            <span>{Math.round(Number(product.offer.discount_percent))}% {t("off")}</span>
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="font-serif text-lg text-charcoal leading-tight" data-testid={`product-name-${product.id}`}>
          {displayName}
        </h3>
        {displayDescription && (
          <p
            className="mt-0.5 text-xs text-charcoal/55 leading-snug line-clamp-2"
            data-testid={`product-description-${product.id}`}
          >
            {displayDescription}
          </p>
        )}
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-mono text-forest font-semibold text-lg">₹{finalPrice}</span>
          {hasOffer && (
            <span className="font-mono text-xs text-charcoal/40 line-through">₹{basePrice}</span>
          )}
          <span className="text-xs text-charcoal/50">/ {displayUom}</span>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-stretch gap-2">
          <div className="relative flex-1">
            <Input
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid={`qty-input-${product.id}`}
              placeholder={t("qty_placeholder")}
              className="pr-16 h-9 sm:h-10 bg-bone border-black/10 focus-visible:ring-forest"
            />
            <span className="uom-chip absolute right-2 top-1/2 -translate-y-1/2" data-testid={`uom-chip-${product.id}`}>
              {displayUom}
            </span>
          </div>
          <Button
            onClick={addToCart}
            data-testid={`add-to-cart-${product.id}`}
            className="shrink-0 w-full sm:w-auto rounded-md text-white hover:opacity-90 h-8 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm"
            style={{ backgroundColor: "var(--forest)" }}
          >
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" /> {t("add")}
          </Button>
        </div>
      </div>
    </article>
  );
}
